import React, { useState, useEffect } from "react";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import {
  Menu,
  MenuItem,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Tooltip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";

import AppsIcon from "@mui/icons-material/Apps";
import MoreVertIcon from "@mui/icons-material/MoreVert";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SearchIcon from "@mui/icons-material/Search";

import SuiBox from "components/SuiBox";
import SuiButton from "components/SuiButton";
import SuiTypography from "components/SuiTypography";

import TableTopControls from "common/TableTopControls";

import ClientesApi from "api/clientesApi";

import ApoderadoClienteModal from "./apoderadoCliente";
import CreateCliente from "./createCliente";
import EditClienteModal from "./editClienteModal";

const ClientesTable = () => {
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalApoderadosOpen, setModalApoderadosOpen] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);

  const handleMenuClick = (event, id) => {
    setAnchorEl(event.currentTarget);
    setSelectedId(id);
    let cliente = clientes.find((a) => a.id === id);
    setSelectedCliente(cliente);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedId(null);
  };

  const handleApoderados = (cliente) => {
    setSelectedCliente(cliente);
    setModalApoderadosOpen(true);
    handleMenuClose();
  };

  const handleEdit = (cliente) => {
    setSelectedCliente(cliente);
    setEditModalOpen(true);
    handleMenuClose();
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro(a) de eliminar este cliente?")) {
      try {
        await ClientesApi.deleteCliente(id);
        fetchClientes();
      } catch (error) {
        console.error("Error al eliminar cliente:", error);
      }
    }
    handleMenuClose();
  };

  const fetchClientes = async () => {
    try {
      const response = await ClientesApi.fetchClientes();
      setClientes(response.data);
    } catch (error) {
      console.error("Error al obtener clientes:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClientes();
  }, []);

  const filteredClientes = clientes.filter((cliente) => {
    const matchesSearch =
      cliente.nombres.toLowerCase().includes(searchTerm.toLowerCase()) ||
      cliente.apellidos.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(cliente.identificacion).includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const paginatedClientes = filteredClientes.slice(page * pageSize, (page + 1) * pageSize);

  const columns = [
    {
      field: "actions",
      renderHeader: () => <AppsIcon />,
      width: 50,
      sortable: false,
      align: "left",
      disableColumnMenu: true,
      resizable: false,
      renderCell: (params) => (
        <IconButton onClick={(e) => handleMenuClick(e, params.row.id)} sx={{ padding: "4px" }}>
          <MoreVertIcon />
        </IconButton>
      ),
    },
    {
      field: "identificacion",
      headerName: "Identificacion",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.tipo_identificacion} - {params.row.identificacion}
        </SuiTypography>
      ),
    },
    {
      field: "nombres",
      headerName: "Nombre",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.nombres} {params.row.apellidos || ""}
        </SuiTypography>
      ),
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.email || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "celular",
      headerName: "Celular",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.celular || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "genero",
      headerName: "Genero",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.genero || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "tipo_trabajador",
      headerName: "Tipo",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.tipo_trabajador || "No tiene."}
        </SuiTypography>
      ),
    },
    {
      field: "sector_trabajador",
      headerName: "Sector",
      flex: 1,
      renderCell: (params) => (
        <SuiTypography variant="caption" fontWeight="medium">
          {params.row.sector_trabajador || "No tiene."}
        </SuiTypography>
      ),
    },
  ];

  return (
    <SuiBox>
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
          boxShadow: "none",
          backgroundColor: "transparent",
        }}
      >
        <SuiBox
          justifyContent="space-between"
          alignItems="center"
          p={1}
          sx={{
            borderBottom: ({ borders: { borderWidth, borderColor } }) =>
              `${borderWidth[1]} solid ${borderColor}`,
          }}
        >
          <Grid container columnSpacing={{ xs: 1, sm: 1, md: 1 }}>
            <Grid size={6}>
              <SuiBox display="flex" flexDirection="column">
                <strong>Administración de Clientes</strong>
              </SuiBox>
            </Grid>
          </Grid>
          <SuiBox display="flex" alignItems="center" gap={1}>
            <Tooltip title="Buscar por Nombre y Identificación" arrow>
              <TextField
                size="small"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPage(0);
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Tooltip>
            <TableTopControls
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalRows={filteredClientes.length}
            />
            <SuiButton
              variant="gradient"
              buttonColor="dark"
              size="small"
              onClick={() => {
                setSelectedCliente(null);
                setShowCreateForm(true);
              }}
            >
              Nuevo Cliente
            </SuiButton>
          </SuiBox>
        </SuiBox>

        <DataGrid
          rows={paginatedClientes}
          columns={columns}
          disableSelectionOnClick
          loading={loading}
          getRowHeight={() => "auto"}
          sx={{
            border: 0,
            backgroundColor: "white",
            borderRadius: 2,
            p: 2,
            [`& .${gridClasses.cell}`]: {
              borderBottom: "none",
              padding: "2px 2px",
            },
            [`& .${gridClasses.row}`]: {
              backgroundColor: ({ palette: { white } }) => white.main,
              "&:hover": {
                backgroundColor: ({ palette: { grey } }) => grey[100],
              },
              minHeight: "48px !important",
            },
            [`& .${gridClasses.columnHeaders}`]: {
              backgroundColor: ({ palette: { grey } }) => grey[50],
              borderRadius: 1,
              minHeight: "48px !important",
            },
            [`& .${gridClasses.footerContainer}`]: {
              display: "none",
            },
          }}
          hideFooter
        />
      </Paper>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem onClick={() => handleApoderados(clientes.find((a) => a.id === selectedId))}>
          <AddCircleOutlineIcon title="APODERADOS" fontSize="small" sx={{ mr: 1 }} /> Apoderados
        </MenuItem>
        <MenuItem onClick={() => handleEdit(clientes.find((a) => a.id === selectedId))}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Editar
        </MenuItem>
        <MenuItem onClick={() => handleDelete(selectedId)} sx={{ color: "error.main" }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Eliminar
        </MenuItem>
      </Menu>

      <ApoderadoClienteModal
        open={modalApoderadosOpen}
        onClose={() => setModalApoderadosOpen(false)}
        cliente={selectedCliente}
        onSuccess={() => {
          fetchClientes();
          setModalApoderadosOpen(false);
        }}
      />
      <CreateCliente
        open={showCreateForm}
        onClose={() => setShowCreateForm(false)}
        onSuccess={() => {
          fetchClientes();
          setShowCreateForm(false);
        }}
      />
      <EditClienteModal
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        cliente={selectedCliente}
        onSuccess={() => {
          fetchClientes();
          setEditModalOpen(false);
        }}
      />
    </SuiBox>
  );
};

export default ClientesTable;
